import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Wrench, Search, X, Loader2, RefreshCw,
  Phone, MapPin, User, CheckCircle2,
  Clock, Zap, Star, Inbox, XCircle,
  Plus, ChevronRight
} from 'lucide-react'
import { taskService } from '@/services/firebase/tasks'
import { useAuthStore } from '@/store/authStore'
import { ROUTES } from '@/constants'
import type { Task, TaskStatus } from '@/types'
import toast from 'react-hot-toast'

// ── Status config (installation pipeline stages) ──────────────────────────────
const STATUS_CFG: Record<TaskStatus, { label: string; bg: string; text: string; border: string; icon: React.ReactNode; dot: string }> = {
  open:             { label: 'Open',             bg: 'bg-sky-50',     text: 'text-sky-700',     border: 'border-sky-200',     icon: <Inbox size={10} />,        dot: '#0ea5e9' },
  assigned:         { label: 'Assigned',         bg: 'bg-blue-50',    text: 'text-blue-700',    border: 'border-blue-200',    icon: <User size={10} />,         dot: '#3b82f6' },
  in_progress:      { label: 'In Progress',      bg: 'bg-violet-50',  text: 'text-violet-700',  border: 'border-violet-200',  icon: <Zap size={10} />,          dot: '#8b5cf6' },
  site_visit:       { label: 'Site Visit',       bg: 'bg-indigo-50',  text: 'text-indigo-700',  border: 'border-indigo-200',  icon: <MapPin size={10} />,       dot: '#6366f1' },
  work_in_progress: { label: 'Work In Progress', bg: 'bg-orange-50',  text: 'text-orange-700',  border: 'border-orange-200',  icon: <Zap size={10} />,          dot: '#f97316' },
  pending_approval: { label: 'Pending Approval', bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200',   icon: <Clock size={10} />,        dot: '#f59e0b' },
  pending_feedback: { label: 'Pending Feedback', bg: 'bg-purple-50',  text: 'text-purple-700',  border: 'border-purple-200',  icon: <Star size={10} />,         dot: '#a855f7' },
  closed:           { label: 'Completed',        bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: <CheckCircle2 size={10} />, dot: '#10b981' },
  cancelled:        { label: 'Cancelled',        bg: 'bg-gray-50',    text: 'text-gray-500',    border: 'border-gray-200',    icon: <XCircle size={10} />,      dot: '#9ca3af' },
}

const STATUS_THEME: Record<TaskStatus, { color: string; shadow: string }> = {
  open:             { color: '#0ea5e9', shadow: 'rgba(14,165,233,0.08)' },
  assigned:         { color: '#3b82f6', shadow: 'rgba(59,130,246,0.08)' },
  in_progress:      { color: '#8b5cf6', shadow: 'rgba(139,92,246,0.08)' },
  site_visit:       { color: '#6366f1', shadow: 'rgba(99,102,241,0.08)' },
  work_in_progress: { color: '#f97316', shadow: 'rgba(249,115,22,0.08)' },
  pending_approval: { color: '#f59e0b', shadow: 'rgba(245,158,11,0.15)' },
  pending_feedback: { color: '#a855f7', shadow: 'rgba(168,85,247,0.08)' },
  closed:           { color: '#10b981', shadow: 'rgba(16,185,129,0.06)' },
  cancelled:        { color: '#9ca3af', shadow: 'rgba(156,163,175,0.04)' },
}

const PRIORITY_CFG: Record<string, { bar: string; badge: string; text: string }> = {
  low:    { bar: '#94a3b8', badge: 'bg-gray-100',    text: 'text-gray-500'   },
  medium: { bar: '#3b82f6', badge: 'bg-blue-100',    text: 'text-blue-700'   },
  high:   { bar: '#f97316', badge: 'bg-orange-100',  text: 'text-orange-700' },
  urgent: { bar: '#ef4444', badge: 'bg-red-100',     text: 'text-red-700'    },
}

// Pipeline stages shown in the filter strip
const STAGE_FILTERS: { label: string; value: TaskStatus | 'all' }[] = [
  { label: 'All',              value: 'all'              },
  { label: 'Open',             value: 'open'             },
  { label: 'Assigned',         value: 'assigned'         },
  { label: 'Site Visit',       value: 'site_visit'       },
  { label: 'Work In Progress', value: 'work_in_progress' },
  { label: 'Approval',         value: 'pending_approval' },
  { label: 'Completed',        value: 'closed'           },
]

// ── Pipeline progress bar ─────────────────────────────────────────────────────
const STAGES: TaskStatus[] = ['open','assigned','in_progress','site_visit','work_in_progress','pending_approval','pending_feedback','closed']
const PipelineBar = ({ status }: { status: TaskStatus }) => {
  const idx = STAGES.indexOf(status)
  const isCancelled = status === 'cancelled'
  return (
    <div className="flex gap-0.5 mt-2">
      {STAGES.map((s, i) => (
        <div key={s} className="flex-1 h-1 rounded-full transition-all"
          style={{ background: isCancelled ? '#e5e7eb' : i <= idx ? STATUS_CFG[s].dot : '#e5e7eb' }} />
      ))}
    </div>
  )
}

// ── Installation card ─────────────────────────────────────────────────────────
const InstCard = ({ task, onClick }: { task: Task; onClick: () => void }) => {
  const s = STATUS_CFG[task.status]
  const p = PRIORITY_CFG[task.priority] ?? PRIORITY_CFG.medium
  const initials = task.customerName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()

  const theme = STATUS_THEME[task.status]

  return (
    <div onClick={onClick}
      className="group bg-white rounded-3xl overflow-hidden cursor-pointer transition-all duration-200 hover:-translate-y-0.5 active:scale-[0.98] border-l-[4px]"
      style={{
        boxShadow: `0 2px 12px ${theme.shadow}, 0 0 0 1px rgba(0,0,0,0.04)`,
        borderLeftColor: theme.color
      }}>

      {/* Dark gradient header */}
      <div className="relative px-4 pt-4 pb-3 overflow-hidden"
        style={{ background: 'linear-gradient(135deg,#050d1f 0%,#091a38 100%)' }}>
        <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: p.bar }} />
        <div className="absolute -top-6 -right-6 w-28 h-28 rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle,rgba(6,182,212,0.4) 0%,transparent 70%)', filter: 'blur(16px)' }} />

        <div className="relative z-10 flex items-start justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 rounded-2xl shrink-0 flex items-center justify-center text-white text-sm font-black"
              style={{ background: 'linear-gradient(135deg,#2563eb,#06b6d4)', boxShadow: '0 4px 12px rgba(6,182,212,0.45)' }}>
              {initials}
            </div>
            <div className="min-w-0">
              <p className="font-black text-white text-[15px] truncate leading-tight">{task.customerName}</p>
              <p className="text-[10px] font-mono text-cyan-300/70 mt-0.5 tracking-wider">{task.taskNumber}</p>
            </div>
          </div>
          <span className={`shrink-0 flex items-center gap-1 text-[10px] font-black px-2.5 py-1 rounded-full border ${s.bg} ${s.text} ${s.border}`}>
            {s.icon} {s.label}
          </span>
        </div>

        <div className="relative z-10 mt-3">
          <PipelineBar status={task.status} />
        </div>
      </div>

      {/* Body */}
      <div className="px-4 py-3 space-y-1.5">
        <div className="flex items-center gap-2 text-[12px] text-gray-600">
          <Phone size={11} className="text-gray-400 shrink-0" />
          <span className="font-semibold">+91 {task.customerPhone}</span>
        </div>
        <div className="flex items-center gap-2 text-[12px] text-gray-500 truncate">
          <MapPin size={11} className="text-gray-400 shrink-0" />
          <span className="truncate">{task.address}{task.city ? `, ${task.city}` : ''}</span>
        </div>
        {task.assignedEngineerName && (
          <div className="flex items-center gap-2 text-[11px] text-blue-600">
            <User size={11} className="shrink-0" />
            <span className="truncate font-semibold">{task.assignedEngineerName}</span>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-1">
          <span className={`text-[10px] font-black px-2.5 py-1 rounded-full ${p.badge} ${p.text} capitalize`}>
            {task.priority}
          </span>
          {task.status === 'closed' && task.customerRating && (
            <div className="flex items-center gap-0.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <span key={i} className={`text-xs leading-none ${i < task.customerRating! ? 'text-amber-400' : 'text-gray-200'}`}>★</span>
              ))}
            </div>
          )}
          <ChevronRight size={13} className="text-gray-300 group-hover:text-gray-500 transition-colors ml-auto" />
        </div>
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────
const InstallationListPage = () => {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const isAdmin = user?.role === 'admin'

  const [tasks, setTasks]         = useState<Task[]>([])
  const [loading, setLoading]     = useState(true)
  const [search, setSearch]       = useState('')
  const [filter, setFilter]       = useState<TaskStatus | 'all'>(
    location.state?.status || 'all'
  )

  useEffect(() => {
    if (location.state?.status) {
      setFilter(location.state.status)
    }
  }, [location.state?.status])

  const load = useCallback(async () => {
    setLoading(true)
    const res = await taskService.list()
    if (res.success && res.data) {
      setTasks(res.data.filter(t => t.type === 'new_connection'))
    } else {
      toast.error('Failed to load installations')
    }
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  const filtered = tasks.filter(t => {
    const matchFilter = filter === 'all' || t.status === filter
    const q = search.toLowerCase()
    const matchSearch = !q || [t.customerName, t.customerPhone, t.taskNumber, t.address, t.city, t.assignedEngineerName]
      .some(v => v?.toLowerCase().includes(q))
    return matchFilter && matchSearch
  })

  // Stats
  const total = tasks.length
  const active = tasks.filter(t => !['closed','cancelled'].includes(t.status)).length
  const completed = tasks.filter(t => t.status === 'closed').length
  const pendingApproval = tasks.filter(t => t.status === 'pending_approval').length

  return (
    <div className="space-y-5 pb-10">

      {/* ── Hero ── */}
      <div className="relative overflow-hidden rounded-3xl text-white"
        style={{ background: 'linear-gradient(135deg,#050d1f 0%,#0c1e3d 50%,#0d2a50 100%)' }}>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-10 -right-10 w-56 h-56 rounded-full"
            style={{ background: 'radial-gradient(circle,rgba(6,182,212,0.35) 0%,transparent 70%)', filter: 'blur(40px)' }} />
          <div className="absolute bottom-0 left-1/3 w-40 h-28 rounded-full"
            style={{ background: 'radial-gradient(circle,rgba(37,99,235,0.25) 0%,transparent 70%)', filter: 'blur(30px)' }} />
        </div>
        <div className="relative z-10 p-5 lg:p-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/10 rounded-full px-3 py-1 mb-3">
              <Wrench size={11} className="text-cyan-300" />
              <span className="text-[11px] text-blue-200 font-semibold uppercase tracking-wider">New Connections</span>
            </div>
            <h1 className="text-xl lg:text-2xl font-black">Installations</h1>
            <p className="text-blue-300/70 text-sm mt-1">{total} installation task{total !== 1 ? 's' : ''} · {active} active</p>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <button onClick={() => navigate(ROUTES.NEW_TASK, { state: { type: 'new_connection' } })}
                className="flex items-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-black transition-all hover:scale-[1.03]"
                style={{ background: 'linear-gradient(135deg,#2563eb,#06b6d4)', boxShadow: '0 4px 16px rgba(6,182,212,0.3)' }}>
                <Plus size={15} /> New Installation
              </button>
            )}
            <button onClick={load}
              className="p-2.5 bg-white/10 rounded-xl hover:bg-white/20 transition-all" title="Refresh">
              <RefreshCw size={15} className={`text-blue-200 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* ── Stats bar ── */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Total',      value: total,           color: 'text-gray-800',    bg: 'bg-white'        },
          { label: 'Active',     value: active,          color: 'text-blue-700',    bg: 'bg-blue-50'      },
          { label: 'Completed',  value: completed,       color: 'text-emerald-700', bg: 'bg-emerald-50'   },
          { label: 'Approval',   value: pendingApproval, color: 'text-amber-700',   bg: 'bg-amber-50'     },
        ].map(m => (
          <div key={m.label} className={`${m.bg} rounded-2xl px-3 py-3 text-center`}
            style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.05), 0 0 0 1px rgba(0,0,0,0.04)' }}>
            <p className={`text-xl font-black ${m.color}`}>{m.value}</p>
            <p className="text-[9px] font-bold text-gray-400 mt-0.5">{m.label}</p>
          </div>
        ))}
      </div>

      {/* ── Search + filter ── */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search customer, phone, task ID…"
            className="w-full pl-9 pr-9 py-2.5 text-sm bg-white border border-gray-200 rounded-2xl outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all text-gray-700" />
          {search && (
            <button onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X size={13} />
            </button>
          )}
        </div>
      </div>

      {/* ── Stage filter pills ── */}
      <div className="-mx-4 flex gap-2 overflow-x-auto scrollbar-hide px-4 pb-1">
        {STAGE_FILTERS.map(f => {
          const cnt = f.value === 'all' ? tasks.length : tasks.filter(t => t.status === f.value).length
          return (
            <button key={f.value} onClick={() => setFilter(f.value)}
              className="shrink-0 flex items-center gap-1.5 text-[11px] font-black px-3.5 py-2 rounded-2xl transition-all active:scale-95"
              style={filter === f.value
                ? { background: 'linear-gradient(135deg,#2563eb,#06b6d4)', color: '#fff', boxShadow: '0 4px 12px rgba(37,99,235,0.35)' }
                : { background: '#f9fafb', color: '#6b7280', border: '1px solid #e5e7eb' }}>
              {f.label}
              {cnt > 0 && (
                <span className="text-[9px] px-1.5 py-0.5 rounded-full font-black"
                  style={{ background: filter === f.value ? 'rgba(255,255,255,0.2)' : '#e5e7eb', color: filter === f.value ? '#fff' : '#4b5563' }}>
                  {cnt}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* ── Content ── */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-24 gap-3">
          <div className="w-14 h-14 rounded-3xl flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg,rgba(37,99,235,0.1),rgba(6,182,212,0.08))' }}>
            <Loader2 size={22} className="text-blue-500 animate-spin" />
          </div>
          <p className="text-sm text-gray-400 font-medium">Loading installations…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 gap-3">
          <div className="w-16 h-16 rounded-3xl flex items-center justify-center bg-gray-50">
            <Wrench size={24} className="text-gray-300" />
          </div>
          <p className="text-sm font-bold text-gray-500">No installations found</p>
          <p className="text-xs text-gray-400">
            {search ? 'Try a different search term' : filter !== 'all' ? 'No tasks in this stage' : 'Create a new connection task to get started'}
          </p>
          {isAdmin && !search && filter === 'all' && (
            <button onClick={() => navigate(ROUTES.NEW_TASK, { state: { type: 'new_connection' } })}
              className="flex items-center gap-2 mt-2 px-4 py-2.5 text-sm font-black text-white rounded-2xl transition-all hover:scale-[1.02]"
              style={{ background: 'linear-gradient(135deg,#2563eb,#06b6d4)' }}>
              <Plus size={14} /> New Installation
            </button>
          )}
        </div>
      ) : (
        <>
          <p className="text-xs text-gray-400 font-medium">
            {filtered.length} installation{filtered.length !== 1 ? 's' : ''}
            {filter !== 'all' && ` · ${STATUS_CFG[filter as TaskStatus]?.label}`}
            {search && ` matching "${search}"`}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(task => (
              <InstCard key={task.id} task={task}
                onClick={() => navigate(`/tasks/${task.id}`)} />
            ))}
          </div>
        </>
      )}
    </div>
  )
}

export default InstallationListPage
